import { useEffect, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MonitorSmartphone, Tablet } from "lucide-react";

import { Button } from "@gately/ui/button";
import { ConfirmDialog } from "@gately/ui/confirm-dialog";
import { TableShell, Tbody, Td, Th, Thead } from "@gately/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@gately/ui/dialog";
import { EmptyState } from "@gately/ui/empty-state";
import { Input } from "@gately/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@gately/ui/select";
import { SettingsSection } from "@gately/ui/settings";
import { StatusBadge } from "@gately/ui/status-badge";
import { toast } from "@gately/ui/toast";

import {
  deviceKindLabel,
  devicePresence,
  displayModeLabel,
  pairingOrgType,
} from "~/lib/device-presence";
import { orpc } from "~/lib/orpc";
import { capitalize, useTerms } from "~/lib/use-terms";

export const Route = createFileRoute("/_dashboard/reglages/appareils")({
  loader: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(
      orpc.me.get.queryOptions(),
    );
    await Promise.all([
      context.queryClient.ensureQueryData(orpc.gateDevice.list.queryOptions()),
      context.queryClient.ensureQueryData(orpc.gate.list.queryOptions()),
      // Only staff pick an org at pairing time; the org list is ownerProcedure.
      ...(me.isOwner
        ? [
            context.queryClient.ensureQueryData(
              orpc.organization.list.queryOptions(),
            ),
          ]
        : []),
    ]);
  },
  component: DevicesPage,
});

const STATUS_TONE = {
  pending: "warning",
  active: "success",
  revoked: "danger",
} as const;

const STATUS_LABEL = {
  pending: "En attente",
  active: "Actif",
  revoked: "Révoqué",
} as const;

function DevicesPage() {
  const { data: me } = useSuspenseQuery(orpc.me.get.queryOptions());
  const { data: devices } = useSuspenseQuery({
    ...orpc.gateDevice.list.queryOptions(),
    // « Hors ligne » is time-derived, so the row has to keep refreshing while
    // the page sits open — the borne pings every 60 s (spec §2.4, §5.5).
    refetchInterval: 60_000,
  });
  const { data: gates } = useSuspenseQuery(orpc.gate.list.queryOptions());
  const queryClient = useQueryClient();
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [pairOpen, setPairOpen] = useState(false);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.gateDevice.list.key() });

  const bindGate = useMutation(
    orpc.gateDevice.bindGate.mutationOptions({
      onSuccess: async () => {
        toast.success("Portail mis à jour.");
        await invalidate();
      },
      onError: (e) => toast.error(e.message || "Échec de la mise à jour."),
    }),
  );

  const revoke = useMutation(
    orpc.gateDevice.revoke.mutationOptions({
      onSuccess: async () => {
        toast.success("Appareil révoqué.");
        setRevokeId(null);
        await invalidate();
      },
      onError: (e) => {
        toast.error(e.message || "Échec de la révocation.");
        setRevokeId(null);
      },
    }),
  );

  const setDisplayMode = useMutation(
    orpc.gateDevice.setDisplayMode.mutationOptions({
      onSuccess: async () => {
        toast.success("Affichage mis à jour.");
        await invalidate();
      },
      onError: (e) => toast.error(e.message || "Échec de la mise à jour."),
    }),
  );
  const activeOrgType = me.activeOrg?.type ?? null;
  // Reading Date.now() straight in the render body is impure
  // (react-hooks/purity); tick it via state on the same cadence as the
  // list's refetch so « hors ligne » stays current while the page is open.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  return (
    <>
      <div className="mb-5 flex items-start justify-between gap-4">
        <SettingsSection
          title="Appareils"
          hint="Téléphones et postes partagés installés aux portails."
        />
        <Button onClick={() => setPairOpen(true)}>Ajouter un appareil</Button>
      </div>

      {/* The station has no discoverable entry point of its own: it lives
          outside this console's shell on purpose, and the browser that runs
          it is a lobby machine nobody navigates by hand. This is where
          somebody setting one up already is.

          One instruction for everyone: `gateDevice.approve` is an
          `adminProcedure`, so a syndic registers a machine into their own org
          and Gately staff into any. The copy used to split by role because
          only staff could do it. */}
      <div className="bg-card mt-6 flex flex-wrap items-center justify-between gap-4 rounded-lg border p-4">
        <div className="flex items-start gap-3">
          <MonitorSmartphone
            className="text-muted-foreground mt-0.5 size-5 shrink-0"
            aria-hidden="true"
          />
          <div>
            <p className="text-sm font-medium">Poste d&apos;accueil</p>
            <p className="text-muted-foreground mt-0.5 text-sm">
              Un ordinateur au poste d&apos;accueil, avec un lecteur de QR
              branché. Ouvrez cette page sur la machine du poste, puis
              enregistrez-la avec « Ajouter un appareil ».
            </p>
          </div>
        </div>
        <Button variant="outline" asChild>
          <Link to="/poste">Ouvrir le poste</Link>
        </Button>
      </div>

      {activeOrgType === "office" ? (
        <div className="bg-card mt-3 flex flex-wrap items-center justify-between gap-4 rounded-lg border p-4">
          <div className="flex items-start gap-3">
            <Tablet
              className="text-muted-foreground mt-0.5 size-5 shrink-0"
              aria-hidden="true"
            />
            <div>
              <p className="text-sm font-medium">Borne d&apos;accueil</p>
              <p className="text-muted-foreground mt-0.5 text-sm">
                Une tablette sur le comptoir où chaque visiteur
                s&apos;enregistre. Ouvrez /borne sur la tablette, puis
                enregistrez-la avec « Ajouter un appareil » en choisissant «
                Borne d&apos;accueil ». Sur iPad, activez l&apos;Accès guidé
                pour garder Safari sur la page.
              </p>
            </div>
          </div>
          <Button variant="outline" asChild>
            <Link to="/borne">Ouvrir la borne</Link>
          </Button>
        </div>
      ) : null}

      {devices.length === 0 ? (
        <div className="border-input mt-6 rounded-xl border border-dashed">
          <EmptyState
            icon={<MonitorSmartphone className="size-6" strokeWidth={1.75} />}
            title="Aucun appareil."
            description="Contactez Gately pour en provisionner un."
            className="py-14"
          />
        </div>
      ) : (
        <TableShell className="mt-6">
          <Thead>
            <tr>
              <Th>Appareil</Th>
              <Th>Type</Th>
              <Th>Portail</Th>
              <Th>Affichage</Th>
              <Th>Statut</Th>
              <Th>Vu le</Th>
              <Th>
                <span className="sr-only">Actions</span>
              </Th>
            </tr>
          </Thead>
          <Tbody>
            {devices.map((d) => (
              <tr key={d.id}>
                <Td className="max-w-0 truncate font-medium">
                  {d.label ?? "Sans nom"}
                </Td>
                <Td className="text-sm">{deviceKindLabel(d.kind)}</Td>
                <Td>
                  <Select
                    value={d.gateId ?? undefined}
                    disabled={d.status !== "active"}
                    onValueChange={(gateId) =>
                      bindGate.mutate({ deviceId: d.id, gateId })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir un portail" />
                    </SelectTrigger>
                    <SelectContent>
                      {gates.map((g) => (
                        <SelectItem key={g.id} value={g.id}>
                          {g.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Td>
                <Td>
                  {d.kind === "borne" ? (
                    <Select
                      value={d.displayMode ?? undefined}
                      disabled={
                        d.status !== "active" || setDisplayMode.isPending
                      }
                      onValueChange={(v) =>
                        setDisplayMode.mutate({
                          deviceId: d.id,
                          displayMode: v as "complet" | "qr",
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="complet">
                          {displayModeLabel("complet")}
                        </SelectItem>
                        <SelectItem value="qr">
                          {displayModeLabel("qr")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </Td>
                <Td>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <StatusBadge tone={STATUS_TONE[d.status]}>
                      {STATUS_LABEL[d.status]}
                    </StatusBadge>
                    {devicePresence(d, nowMs) === "offline" ? (
                      <StatusBadge tone="danger">Hors ligne</StatusBadge>
                    ) : devicePresence(d, nowMs) === "online" ? (
                      <StatusBadge tone="success">En ligne</StatusBadge>
                    ) : null}
                  </div>
                </Td>
                <Td className="text-muted-foreground text-sm">
                  {d.lastSeenAt
                    ? new Date(d.lastSeenAt).toLocaleString("fr-FR", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })
                    : "—"}
                </Td>
                <Td>
                  <div className="flex justify-end">
                    {d.status !== "revoked" ? (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setRevokeId(d.id)}
                      >
                        Révoquer
                      </Button>
                    ) : null}
                  </div>
                </Td>
              </tr>
            ))}
          </Tbody>
        </TableShell>
      )}

      <PairingDialog
        isOwner={me.isOwner}
        activeOrgType={activeOrgType}
        open={pairOpen}
        onOpenChange={setPairOpen}
        onPaired={invalidate}
      />

      <ConfirmDialog
        open={revokeId !== null}
        onOpenChange={(o) => !o && setRevokeId(null)}
        title="Révoquer cet appareil ?"
        description="L'appareil sera déconnecté immédiatement et tout service en cours sera terminé. Il faudra le ré-appairer pour le réutiliser."
        confirmLabel="Révoquer"
        pendingLabel="Révocation…"
        destructive
        pending={revoke.isPending}
        onConfirm={() => revokeId && revoke.mutate({ deviceId: revokeId })}
      />
    </>
  );
}

function PairingDialog({
  isOwner,
  activeOrgType,
  open,
  onOpenChange,
  onPaired,
}: {
  /** Gately staff pair across tenants and pick the org; a syndic pairs into
   *  their own active org, so `gateDevice.approve` gets no `organizationId`. */
  isOwner: boolean;
  activeOrgType: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPaired: () => Promise<void> | void;
}) {
  // Staff pick any org; a syndic pairs into their active org, so no org
  // picker and no organization.list call (ownerProcedure — it would 403).
  // `useQuery`, not `useSuspenseQuery`: the suspense variant can't be disabled.
  const { data: orgs } = useQuery({
    ...orpc.organization.list.queryOptions(),
    enabled: isOwner,
  });
  const { data: myGates } = useQuery({
    ...orpc.gate.list.queryOptions(),
    enabled: !isOwner,
  });
  // Staff pair across verticals, so their copy says "organisation"; a syndic
  // or office admin pairs into one org and reads it in its own words.
  const terms = useTerms();
  const [userCode, setUserCode] = useState("");
  const [orgId, setOrgId] = useState<string | undefined>(undefined);
  const [gateId, setGateId] = useState<string | undefined>(undefined);
  const [label, setLabel] = useState("");
  const [kind, setKind] = useState<"gate" | "borne">("gate");
  const [displayMode, setDisplayMode] = useState<"complet" | "qr">("complet");
  const targetType = pairingOrgType({ isOwner, activeOrgType, orgs, orgId });
  const canBeBorne = targetType === "office";

  // Gates are scoped to the chosen résidence, so they can only load once an
  // org is picked. Changing the org clears any stale gate selection.
  const { data: orgGates } = useQuery({
    ...orpc.gate.listForOrg.queryOptions({
      input: { organizationId: orgId ?? "" },
    }),
    enabled: isOwner && !!orgId,
  });
  const gates = isOwner ? orgGates : myGates;

  const approve = useMutation(
    orpc.gateDevice.approve.mutationOptions({
      onSuccess: async () => {
        toast.success("Appareil appairé.");
        setUserCode("");
        setOrgId(undefined);
        setGateId(undefined);
        setLabel("");
        setKind("gate");
        setDisplayMode("complet");
        onOpenChange(false);
        await onPaired();
      },
      onError: (e) =>
        toast.error(
          (e as { message?: string }).message ?? "Échec de l'appairage.",
        ),
    }),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Appairer un appareil</DialogTitle>
          <DialogDescription>
            {isOwner
              ? "Saisissez le code affiché sur le téléphone, puis choisissez l'organisation et le portail."
              : "Saisissez le code affiché sur l'appareil, puis choisissez le portail."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label
              htmlFor="pair-code"
              className="text-foreground block text-sm font-medium"
            >
              Code d'appairage
            </label>
            <Input
              id="pair-code"
              value={userCode}
              onChange={(e) => setUserCode(e.target.value.toUpperCase().trim())}
              placeholder="XXXXXXXX"
              autoFocus
            />
          </div>
          {isOwner ? (
            <div className="space-y-1.5">
              <label
                htmlFor="pair-org"
                className="text-foreground block text-sm font-medium"
              >
                Organisation
              </label>
              <Select
                value={orgId}
                onValueChange={(v) => {
                  setOrgId(v);
                  setGateId(undefined);
                  setKind("gate");
                  setDisplayMode("complet");
                }}
              >
                <SelectTrigger id="pair-org" className="w-full">
                  <SelectValue placeholder="Choisir une organisation" />
                </SelectTrigger>
                <SelectContent>
                  {(orgs ?? []).map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <div className="space-y-1.5">
            <label
              htmlFor="pair-gate"
              className="text-foreground block text-sm font-medium"
            >
              Portail
            </label>
            <Select
              value={gateId}
              onValueChange={setGateId}
              disabled={isOwner && !orgId}
            >
              <SelectTrigger id="pair-gate" className="w-full">
                <SelectValue
                  placeholder={
                    isOwner && !orgId
                      ? "Choisissez d'abord une organisation"
                      : "Choisir un portail"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {(gates ?? []).map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(!isOwner || orgId) && gates && gates.length === 0 ? (
              <p className="text-xs text-amber-600">
                {isOwner ? "Cette organisation" : capitalize(terms.thisOrg)}{" "}
                n&apos;a aucun portail. Créez-en un avant d&apos;appairer.
              </p>
            ) : null}
          </div>
          {canBeBorne ? (
            <div className="space-y-1.5">
              <label
                htmlFor="pair-kind"
                className="text-foreground block text-sm font-medium"
              >
                Type d&apos;appareil
              </label>
              <Select
                value={kind}
                onValueChange={(v) => setKind(v as "gate" | "borne")}
              >
                <SelectTrigger id="pair-kind" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gate">
                    {deviceKindLabel("gate")}
                  </SelectItem>
                  <SelectItem value="borne">
                    {deviceKindLabel("borne")}
                  </SelectItem>
                </SelectContent>
              </Select>
              {kind === "borne" ? (
                <div className="space-y-1.5">
                  <label
                    htmlFor="pair-display-mode"
                    className="text-foreground block text-sm font-medium"
                  >
                    Affichage
                  </label>
                  <Select
                    value={displayMode}
                    onValueChange={(v) => setDisplayMode(v as "complet" | "qr")}
                  >
                    <SelectTrigger id="pair-display-mode" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="complet">
                        {displayModeLabel("complet")}
                      </SelectItem>
                      <SelectItem value="qr">
                        {displayModeLabel("qr")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-muted-foreground text-xs">
                    Complet : formulaire sur la tablette et QR. QR seul : écran
                    sans clavier.
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="space-y-1.5">
            <label
              htmlFor="pair-label"
              className="text-foreground block text-sm font-medium"
            >
              Nom de l'appareil{" "}
              <span className="text-muted-foreground font-normal">
                (optionnel)
              </span>
            </label>
            <Input
              id="pair-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Portail Nord"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            disabled={
              userCode.length < 4 ||
              !gateId ||
              approve.isPending ||
              (isOwner && !orgId)
            }
            onClick={() =>
              gateId &&
              approve.mutate({
                userCode,
                // Omitted for a syndic: the server binds to their active org.
                organizationId: isOwner ? orgId : undefined,
                gateId,
                label: label.trim() || undefined,
                kind: canBeBorne ? kind : "gate",
                displayMode:
                  canBeBorne && kind === "borne" ? displayMode : undefined,
              })
            }
          >
            {approve.isPending ? "Appairage…" : "Appairer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
